import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { applicationsApi } from '../../services/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import { formatDateTime } from '../../utils/date'
import { useMarkNotificationsRead } from '../../hooks/useMarkNotificationsRead'
import { useAuth } from '../../context/AuthContext'
import { formatCampusLabel } from '../../utils/campuses'

const STATUSES = ['new', 'reviewed', 'contacted', 'accepted', 'declined']

export default function AdminApplicationDetail() {
  const { id } = useParams()
  const { t } = useTranslation()
  const { hasModuleAccess } = useAuth()
  const canDelete = hasModuleAccess('forms_applications', 'can_delete')
  const canEdit = hasModuleAccess('forms_applications', 'can_edit')
  useMarkNotificationsRead('forms_applications', id ? Number(id) : null)

  const [application, setApplication] = useState(null)
  const [status, setStatus] = useState('new')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    applicationsApi
      .get(id)
      .then(({ data }) => {
        const item = data.data || data
        setApplication(item)
        setStatus(item.status || 'new')
      })
      .catch(() => setApplication(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [id])

  const handleStatusUpdate = async () => {
    if (!canEdit) return

    setSaving(true)
    try {
      await applicationsApi.updateStatus(id, { status })
      load()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!canDelete || !window.confirm(t('admin.applications.confirmDelete'))) return

    await applicationsApi.delete(id)
    window.location.href = '/cp-login/forms/applications'
  }

  if (loading) return <LoadingSpinner className="py-20" />
  if (!application) return null

  const detailFields = [
    ['student', application.student_name],
    ['parent', application.parent_name],
    ['email', application.email],
    ['phone', application.phone],
    ['grade', application.grade_applying],
    ['program', application.program],
    ['branches', formatCampusLabel(application.campus, t)],
    ['currentSchool', application.current_school],
    ['message', application.message],
    ['submitted', application.created_at ? formatDateTime(application.created_at) : '—'],
  ]

  return (
    <div>
      <Link
        to="/cp-login/forms/applications"
        className="mb-6 inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('admin.applications.backToList')}
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary">{application.student_name}</h2>
          <p className="text-text/60">{application.program}</p>
        </div>
        {canDelete ? (
          <Button variant="ghost" onClick={handleDelete} className="text-red-600">
            <Trash2 className="h-4 w-4" />
            {t('buttons.delete')}
          </Button>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h3 className="mb-4 font-semibold text-primary">{t('admin.applications.details')}</h3>
          <dl className="grid gap-4 sm:grid-cols-2">
            {detailFields.map(([key, value]) => (
              <div key={key} className={key === 'message' ? 'sm:col-span-2' : ''}>
                <dt className="text-xs font-medium uppercase tracking-wide text-text/50">
                  {t(`admin.applications.${key}`)}
                </dt>
                <dd className="mt-1 whitespace-pre-wrap text-sm text-text">{value || '—'}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card>
          <h3 className="mb-4 font-semibold text-primary">{t('admin.table.status')}</h3>
          {canEdit ? (
            <div className="space-y-4">
              <AdminFormField label={t('admin.table.status')} htmlFor="application-status">
                <select
                  id="application-status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className={adminInputClass}
                >
                  {STATUSES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </AdminFormField>
              <Button onClick={handleStatusUpdate} disabled={saving}>
                {saving ? t('common.loading') : t('admin.applications.saveStatus')}
              </Button>
            </div>
          ) : (
            <p className="text-sm font-medium capitalize text-text">{application.status}</p>
          )}
        </Card>
      </div>
    </div>
  )
}
